import React, { useMemo } from "react";
import { Box, Typography, Link } from "@mui/material";
import { useTranslation } from "react-i18next";

import Checkbox from "Components/Form/Checkbox";
import useStyles from "./styles";
import { getSystemReportsForAvailableIn } from "@/constants/communicationCentreSystemReports";

interface SystemReportsProps {
  availableIn: string[];
  selectedReportNames: string[];
  onSelectedReportNamesChange: (reportNames: string[]) => void;
  disabled?: boolean;
}

const SystemReports: React.FC<SystemReportsProps> = ({
  availableIn,
  selectedReportNames,
  onSelectedReportNamesChange,
  disabled = false,
}) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const visibleReports = useMemo(
    () => getSystemReportsForAvailableIn(availableIn),
    [availableIn]
  );

  const allSelected = useMemo(
    () =>
      visibleReports.length > 0 &&
      visibleReports.every((report) => selectedReportNames.includes(report.reportName)),
    [visibleReports, selectedReportNames]
  );

  if (visibleReports.length === 0) {
    return null;
  }

  const handleToggleReport = (reportName: string, checked: boolean) => {
    if (disabled) return;

    if (checked) {
      if (!selectedReportNames.includes(reportName)) {
        onSelectedReportNamesChange([...selectedReportNames, reportName]);
      }
    } else {
      onSelectedReportNamesChange(selectedReportNames.filter((name) => name !== reportName));
    }
  };

  const handleSelectAll = () => {
    if (disabled) return;
    onSelectedReportNamesChange(visibleReports.map((report) => report.reportName));
  };

  const handleUnselectAll = () => {
    if (disabled) return;
    onSelectedReportNamesChange([]);
  };

  return (
    <Box className={classes.container}>
      <Typography variant="h6" className={classes.sectionTitle}>
        {translate("systemGenerated") || "System generated"}
      </Typography>

      <Box className={classes.reportsList}>
        {visibleReports.map((report) => (
          <Box key={report.reportName} className={classes.reportItem}>
            <Checkbox
              checked={selectedReportNames.includes(report.reportName)}
              onChange={(_, checked) => handleToggleReport(report.reportName, checked)}
              disabled={disabled}
            />
            <Typography className={classes.reportLabel}>{report.reportName}</Typography>
          </Box>
        ))}
      </Box>

      <Box className={classes.actionsRow}>
        <Link
          component="button"
          type="button"
          onClick={handleSelectAll}
          disabled={disabled || allSelected}
          className={classes.actionLink}
        >
          {translate("selectAll") || "Select all"}
        </Link>
        <Link
          component="button"
          type="button"
          onClick={handleUnselectAll}
          disabled={disabled || selectedReportNames.length === 0}
          className={classes.actionLink}
        >
          {translate("unselectAll") || "Unselect all"}
        </Link>
      </Box>
    </Box>
  );
};

export default SystemReports;
